import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import * as styles from './PublicContentSettings.module.scss';
import Button from '../../components/Button';
import Form, { Field, Submit } from '../../components/Form';
import { gql } from '../../graphql/generated/gql';
import type { AllowPublicContentBy } from '../../graphql/generated/graphql';
import PricingModalButton from '../../organization/PricingModalButton';
import SettingCard from '../../settings/SettingCard';
import { isFeatureEnabled } from '../../utils/features';
import useAllowPublicContentBy from '../../utils/useAllowPublicContentBy';

const UPDATE_ALLOW_PUBLIC_CONTENT_BY_MUTATION = gql(/** @complexity 4 */ `
  mutation updatePublicContentBySetting(
    $allowPublicContentBy: AllowPublicContentBy!
  ) {
    organization: updateOrganization(
      settings: { allowPublicContentBy: $allowPublicContentBy }
    ) {
      id
      settings {
        allowPublicContentBy
      }
    }
  }
`);

const options = {
  ONLY_ADMINS: 'Only admins can make posts and topics public',
  ADMIN_TOPICS_USER_POSTS:
    'Only admins can make topics public, anyone can make posts public',
  ALL: 'Anyone can make posts and topics public',
} as const;

type Values = {
  allowPublicContentBy: AllowPublicContentBy | null;
};

const PublicContentSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [updateSettings] = useMutation(UPDATE_ALLOW_PUBLIC_CONTENT_BY_MUTATION);

  const allowPublicContentBy = useAllowPublicContentBy();

  const handleSubmit = async (values: Values) => {
    if (values.allowPublicContentBy) {
      await updateSettings({
        variables: { allowPublicContentBy: values.allowPublicContentBy },
      });
      setIsEditing(false);
    }
  };

  const isPublicPostsTopicsEnabled = isFeatureEnabled('public_posts_topics');
  const description = isPublicPostsTopicsEnabled
    ? !!allowPublicContentBy && options[allowPublicContentBy]
    : 'Public content is not available on your plan';

  return (
    <>
      {isEditing ? (
        <SettingCard
          title="Public topics & posts"
          description={
            <Form<Values>
              initialValues={{ allowPublicContentBy }}
              onSubmit={handleSubmit}
            >
              <div className={styles.formFields}>
                {(
                  ['ALL', 'ADMIN_TOPICS_USER_POSTS', 'ONLY_ADMINS'] as const
                ).map((value) => (
                  <Field
                    key={value}
                    label={options[value]}
                    name="allowPublicContentBy"
                    type="radio"
                    value={value}
                  />
                ))}
              </div>
              <div className={styles.description}>
                Regardless of selection, users must have edit access to the
                topic or post to make it public.
              </div>
              <div className={styles.formFooter}>
                <Submit variant="primary">Save</Submit>
              </div>
            </Form>
          }
        />
      ) : (
        <SettingCard
          actions={
            isPublicPostsTopicsEnabled ? (
              <Button onClick={() => setIsEditing(true)} variant="secondary">
                Edit
              </Button>
            ) : (
              <PricingModalButton feature="advancedTopics">
                Upgrade
              </PricingModalButton>
            )
          }
          title="Public topics & posts"
          description={description}
        />
      )}
    </>
  );
};

export default PublicContentSettings;
